import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { IconButton } from "@material-ui/core";
import AddToPhotosIcon from "@material-ui/icons/AddToPhotos";
import DeleteIcon from "@material-ui/icons/Delete";
import MenuList from "../../components/MenuList";
import { selectAllFiles } from "../../store/fileStore/fileSelectors";
import HorizontalPanel from "../../components/HorizontalPanel";
import { Toolbar } from "../../app/components";
import { FileUploader } from "../../app/components/ui/file-upload-button/FileUploadButton";
import { useTypedDispatch } from "../../app/state";
import { deleteFile, downloadFile, getFilesByQuery, uploadFile } from "../../store/fileStore/fileRequests";
import { useShowModal } from "../../app/shared/modal-context/modal.context";
import { ModalType } from "../../app/shared/modal-context/constants";
import { clearCurrentFiles } from "../../store/fileStore/fileState";
import { useSnackbar } from "../../app/shared/snackbar-context/snackbar.context";
import { MB } from "../../helpers/converters/fileHelpers";

interface FileListContainerProps {
  locationId?: number | null,
  buildingId?: number | null,
  licenceId?: number | null,
  uploadAllowed?: boolean,
  showDelete?: boolean,
}

const FileListContainer = ({
  locationId,
  buildingId,
  licenceId,
  uploadAllowed = false,
  showDelete = false,
}: FileListContainerProps) => {
  const dispatch = useTypedDispatch();
  const { showModal } = useShowModal();
  const { openSnackbar } = useSnackbar();
  const fileList = useSelector(selectAllFiles);

  function updateFileList() {
    dispatch(clearCurrentFiles());
    dispatch(getFilesByQuery(licenceId ? {
      byLicenceId: licenceId ? licenceId : null,
    } : {
      byLocationId: locationId ? locationId : null,
      byBuildingId: buildingId ? buildingId : null,
    }))
  }

  useEffect(() => {
    updateFileList();
  }, [ locationId, buildingId, licenceId ]);

  const handleUpload = (file: File) => {
    if (file.size > MB * 50) {
      openSnackbar({
        message: 'File size is too big',
        title: 'Error uploading',
        severity: "error",
      });
      return;
    }
    if (locationId || buildingId || licenceId) {
      openSnackbar({
        message: 'Upload file has been started',
        title: 'Uploading',
        severity: "info",
      });

      dispatch(uploadFile({
          file,
          locationId: locationId ? [ locationId ] : null,
          buildingId: buildingId ? [ buildingId ] : null,
          licenceId: licenceId ? licenceId : null,
        })
      ).then((action) => {
        // @ts-ignore
        if (action?.error) {
          openSnackbar({
            message: 'There was an error uploading your file. Please, try again later',
            title: 'Upload failed',
            severity: "error",
          });

          return;
        }
        openSnackbar({
          message: 'File has been successfully uploaded',
          title: 'Uploaded',
          severity: "success",
        });

        updateFileList();
      })
    }
  }

  const handleDownload = (id: number) => {
    showModal(ModalType.MODAL_GENERIC_CONFIRM, {
      title: 'Download file',
      text: `Do you want to download the file?`,
      label: 'Reason',
      confirmButtonHandler: () => {
        openSnackbar({
          message: 'Download has been started',
          title: 'System message',
          severity: "warning",
        });
        dispatch(
          downloadFile(id)
        ).then((action) => {
          // @ts-ignore
          if (action?.error) {
            openSnackbar({
              message: 'There was an error uploading your file. Please, try again later',
              title: 'Upload failed',
              severity: "error",
            });

            return;
          }
        })
      },
      confirmButtonText: 'Download',
    });
  }

  const onContinueDelete = (fileId) => {
    dispatch(deleteFile(fileId))
      .then(() => updateFileList())
  }

  const onDeleteClick = (fileId: number) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    showModal(ModalType.MODAL_GENERIC_CONFIRM, {
      title: "Delete file",
      text: "Do you really want to delete this file?",
      confirmButtonHandler: () => onContinueDelete(fileId),
      confirmButtonText: "Delete",
    });
  }

  const itemsToShow = fileList.map((file) => {
    return {
      ...file,
      rightIcon: showDelete ? (
        <IconButton
          size="small"
          color="default"
          onClick={onDeleteClick(file.id)}
        >
          <DeleteIcon />
        </IconButton>
      ) : null,
    }
  });

  return (
    <HorizontalPanel size={HorizontalPanel.SIZES.WHOLE_SPACE}>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <Toolbar title="File list">
          {
            uploadAllowed ? (
              <FileUploader
                handleFile={handleUpload}
                accept="*.*"
                label="upload file"
                icon={<AddToPhotosIcon />}
                disabled={!licenceId || licenceId === -1}
              />
            ) : null
          }
        </Toolbar>
      </HorizontalPanel>
      <HorizontalPanel size={HorizontalPanel.SIZES.REST_SPACE}>
        <MenuList
          items={itemsToShow}
          size="big"
          selectItem={handleDownload}
        />
      </HorizontalPanel>
    </HorizontalPanel>
  );
}

export default FileListContainer;